// THE PAGE REGISTRY — the single place that records what Otto KNOWS about a surface of this platform.
// Each entry says what a page IS, which FAMILY it belongs to, what ENTITY (record noun) it owns, what DATA
// lives there, and which Otto capabilities are mounted on it. This is how the (backend) planner knows that
// "the recommendations" live on the Recommendations page and that sorting a table can only happen there —
// so it can insert a navigation step even when the user never said "go to". The operable subset is sent on
// every /interpret as `app_context.pages` (labels only — privacy boundary).
//
// Data-driven on purpose (the "source affordances from the surface" rule): reachability comes from the LIVE,
// permission-scoped navTargets — a page the user can't open is not in their manifest, so the planner can
// never route a step to it. A NEW page extends Otto by adding ONE entry here; every seam that names pages,
// disambiguates nouns, or redirects a request derives from this map, so a page becomes known to all of them
// at once. The backend keeps a byte-equivalent DEFAULT fallback (app/router/planner.py DEFAULT_PAGES) for
// old clients — kept in sync for the OPERABLE pages by the otto-sync xrepo-page-manifest gate.
//
// TIERS (derived from the fields present, never declared — a declared tier is a second source of truth that
// drifts from the data it describes):
//   1 NAVIGABLE  — label + family only. Otto can open it and answer "where is X", nothing more. Costs one
//                  entry, and NEVER enters an ambiguity offer, so growing to every page in the platform
//                  does not grow the option lists the user sees.
//   2 UNDERSTOOD — + `data` (what records live here) and usually `entity` (the noun the page owns). Otto can
//                  now route to it, redirect another page's data request to it, and OFFER it when a bare noun
//                  is ambiguous — all WITHOUT the assistant being mounted there.
//   3 OPERABLE   — + `capabilities`. Otto is mounted on the page and can act on it. Only these are mirrored
//                  to the backend (see buildPageManifest).
//
// FORMAT CONSTRAINT: the otto-sync parity gate parses this block with a regex — keep every page entry
// MULTI-LINE with its closing brace on its own line, and keep `entity: { ... }` on ONE line. A page's KEY
// must be its navCatalog target key, which for several pages is kebab-case (they mirror the side menu's own
// keys) and therefore quoted here; all three parsers of this file accept a quoted key (otto-sync's
// run-gates.js, filter-ai's scripts/gen_page_mirrors.py and platform_qa/scanner.py) — extend them together
// if the format ever changes again, because two of the three MIS-PARSE rather than fail on a key they don't
// expect.
import { normalizeText } from "../../core/normalizeText";

const PAGE_DEFS = {
    // ---- the RECOMMENDATIONS family: the FCA dashboard and the project hub pages it drills into --------
    dashboard: {
        label: "FCA Dashboard",
        family: "recommendations",
        data: "portfolio KPIs and charts aggregated over recommendation data",
        capabilities: ["filters", "charts", "dashboard_reset", "navigate"]
    },
    recommendations: {
        label: "Recommendations",
        family: "recommendations",
        entity: { singular: "recommendation", plural: "recommendations" },
        data: "the recommendations LIST — a table of individual recommendation records",
        capabilities: ["filters", "table_view", "navigate"]
    },
    buildings: {
        label: "Buildings",
        family: "recommendations",
        entity: { singular: "building", plural: "buildings", chartValue: "buildings" },
        // FTO-95: FCA-6838 added a MyFilters panel to building/index.jsx (imported :46, rendered :5231,
        // submitting through submitMasterFilter :1318) — the same panel machinery Recommendations/Assets
        // use — so `filters` now joins `table_view`. `column_filter` STAYS: the panel filters the QUERY,
        // the grid's own per-column dropdowns still filter rows IN PLACE. See
        // building/ottoBuildingFilters.js for the adapter (its own catalog + param shape, not assets').
        data: "the buildings LIST — a table of individual building records",
        capabilities: ["filters", "table_view", "navigate"]
    },
    // FTO-95: Regions and Sites are PURE-TABLE pages — neither container renders a MyFilters panel (verified
    // 2026-08-17), so unlike `buildings` above they get `table_view` + `navigate` and deliberately NO
    // `filters`. That absence is what routes a value query here to table_view's in-place `column_filter`
    // (see classifier.py's TABLE_FILTER_ADDENDUM) and what makes `refineForPage` describe the page as
    // "Filter and work with the table". Mounts: region/index.jsx + site/index.jsx renderOttoAssistant.
    regions: {
        label: "Regions",
        family: "recommendations",
        entity: { singular: "region", plural: "regions", chartValue: "regions" },
        data: "the regions LIST — a table of the project's geographic regions",
        capabilities: ["table_view", "navigate"]
    },
    sites: {
        label: "Sites",
        family: "recommendations",
        entity: { singular: "site", plural: "sites", chartValue: "sites" },
        data: "the sites LIST — a table of the project's sites",
        capabilities: ["table_view", "navigate"]
    },
    additions: {
        label: "Additions",
        family: "recommendations",
        entity: { singular: "addition", plural: "additions", chartValue: "additions" },
        data: "the building additions LIST — a table of additions belonging to the project's buildings"
    },
    floors: {
        label: "Floors",
        family: "recommendations",
        entity: { singular: "floor", plural: "floors" },
        data: "the floors LIST — a table of floors belonging to the project's buildings"
    },
    // ---- the ASSETS family: the platform's SECOND dashboard and its own hierarchy ----------------------
    // Both started at tier 2 (FTO-68) — known and offerable before Otto could act on them, which is what let
    // "show me my assets" propose the register long before anything was mounted there. They are tier 3 now.
    assetDashboard: {
        label: "Asset Dashboard",
        family: "assets",
        // A dashboard aggregates rather than holding records, so it was first registered WITHOUT an entity.
        // That was wrong in the one place it matters: asked from the FCA Dashboard - itself the aggregate
        // view of recommendations - "show me my assets" most plausibly means the equivalent view of assets,
        // not the register. Both asset pages now own the noun, and ranking decides which leads (the
        // dashboard from a dashboard, the table from a table page).
        entity: { singular: "asset", plural: "assets" },
        // Its filter panel is literally the SAME component + catalog as the Asset Management grid, so the
        // asset filter adapter is reused verbatim (assets/ottoAssetFilters.js).
        data: "portfolio KPIs and charts aggregated over ASSET data (the asset-scoped counterpart of the FCA Dashboard)",
        capabilities: ["filters", "charts", "dashboard_reset", "navigate"]
    },
    // The Assets TABLE VIEW — the asset hub's grid, and the asset-family twin of Recommendations. Like
    // Recommendations it filters through its OWN panel, so `filters` is a real capability and row filtering
    // is NOT table_view's column_filter (the controller omits those deps). Its field catalog, option source
    // and apply shape all differ from the dashboard's — see assets/ottoAssetFilters.js + ottoAssetOptions.js.
    // It OWNS the asset noun: the module landing below is a list of clients, not of assets.
    assets: {
        label: "Assets",
        family: "assets",
        entity: { singular: "asset", plural: "assets" },
        data: "the assets LIST — a table of individual asset records for one client",
        capabilities: ["filters", "table_view", "navigate"]
    },
    // The SAME grid component, embedded in another hub (Region/Site/Building info, Image Management), where
    // it shows assets already scoped to that record. Otto is mounted there and can filter/drive the table,
    // but the asset-hub destinations do NOT apply — you cannot "go to" this page, you arrive by opening the
    // parent record. Hence navigable:false, and deliberately NO `entity`: the bare noun "assets" must keep
    // resolving to the asset hub and the Asset Dashboard, never to a project-hub sub-tab.
    scopedAssets: {
        label: "Assets",
        family: "assets",
        navigable: false,
        data: "an assets list scoped to one region, site, building or image record, shown inside that record's page",
        capabilities: ["filters", "table_view"]
    },
    // The asset hub's OTHER tabs. Registered so the family can tell them apart from the project hub's
    // same-named pages (familyOf is what makes "go to buildings" mean asset buildings here).
    //
    // FTO-95 promoted these two to OPERABLE alongside their project-hub twins above: they are rendered by
    // the SAME containers (region/index.jsx, site/index.jsx), so wiring the container once lit up both hubs
    // — the page key is container × hub, and only the ROUTE's `section` tells them apart. Same capability
    // set as `regions`/`sites` for the same reason (no MyFilters panel on either container).
    assetRegions: {
        label: "Regions",
        family: "assets",
        data: "the client's asset REGIONS — the top level of the asset hierarchy",
        capabilities: ["table_view", "navigate"]
    },
    assetSites: {
        label: "Sites",
        family: "assets",
        data: "the client's asset SITES",
        capabilities: ["table_view", "navigate"]
    },
    // Same shape as the project hub's `buildings` (FTO-93 registered it; FTO-95 gave it `filters` too, for
    // the same reason: FCA-6838's MyFilters panel is mounted on this tab as well). `column_filter` stays.
    assetBuildings: {
        label: "Buildings",
        family: "assets",
        data: "the client's asset BUILDINGS",
        capabilities: ["filters", "table_view", "navigate"]
    },
    mobileAssets: {
        label: "Mobile Assets",
        family: "assets",
        data: "assets captured through the mobile survey app"
    },
    // The ASSET SURVEY PROJECTS grid — the table the side menu reaches as "Mobile Solutions" (FTO-107).
    //
    // Registered because of what its ABSENCE did. The nav catalog has always been able to open it, and the
    // QA inventory has always been able to name it, but with no entry here it was tier 0: not in
    // `app_context.pages`, so the multi-task planner had no legal `target_page` for it. Asked "go to Asset
    // Survey Projects and filter on client Amazon" it did not clarify or refuse — it silently substituted
    // the nearest asset-family page that DID declare filters + table_view and planned two steps against
    // Asset Management (trace 01b65fdb, prod, thumbs-down: "Its not the Asset Management Table its Asset
    // Survey Projects Table located inside Mobile Solutions"). A page Otto can open but cannot reason about
    // is not half-known, it is mis-known, and substitution is the failure mode.
    //
    // The KEY is the nav-catalog target key (one key space, asserted by xrepo-page-nav-keys). The LABEL is
    // the table's own name rather than the menu's: this is what Otto calls the page back to the user
    // ("Asset Survey Projects Table View"), and "Mobile Solutions Table View" names nothing. The menu wording
    // stays a synonym in navCatalog, so "go to mobile solutions" still grounds here.
    "mobile-solutions": {
        label: "Asset Survey Projects",
        family: "assets",
        entity: { singular: "asset survey project", plural: "asset survey projects" },
        data: "the asset SURVEY PROJECTS list — a table of mobile survey projects (reached from the side menu as Mobile Solutions)",
        capabilities: ["filters", "table_view", "navigate"]
    },
    assetClient: {
        label: "Client",
        family: "assets",
        data: "the client's own record — the asset hub's overview tab"
    },
    // The Asset Management LANDING — a list of CLIENTS, not of assets. Understood (nameable, offerable,
    // redirect-able) but not operable: Otto is not mounted on the client list.
    assetmanagement: {
        label: "Asset Management",
        family: "assets",
        data: "the Asset Management client LIST — the entry point to each client's asset hub"
    },
    // ---- the IMAGES family ----------------------------------------------------------------------------
    images: {
        label: "Image Management",
        family: "images",
        entity: { singular: "image", plural: "images" },
        data: "the image LIBRARY — photos captured against buildings, assets, and recommendations"
    }
};

// The three levels of knowledge, derived from what an entry actually declares (see the header note).
export const TIERS = { NAVIGABLE: 1, UNDERSTOOD: 2, OPERABLE: 3 };

const tierOfDef = def =>
    !def ? 0 : (def.capabilities || []).length ? TIERS.OPERABLE : def.data ? TIERS.UNDERSTOOD : TIERS.NAVIGABLE;

// How much Otto knows about `pageKey`: 3 operable / 2 understood / 1 navigable / 0 unknown.
export const tierOf = pageKey => tierOfDef(PAGE_DEFS[pageKey]);

// The FAMILY a page belongs to ("recommendations" | "assets" | "images"), or "" when unknown. The platform
// runs three parallel hierarchies that reuse each other's page names — the project hub and the asset hub BOTH
// have Regions/Sites/Buildings, and Assets appear under both the asset hub and the image hub — so the family,
// not the page name, is what tells two same-named pages apart.
//
// Recorded now, not yet consulted: the seams that offer pages still rank by nav-catalog order. The rule it
// exists for is to resolve a noun WITHIN the current family silently and only ask across families when that
// leaves nothing — which is what will keep an offer to ~3 buttons as the registry grows past the point where
// enumerating candidates is usable. Registering families with the pages means that lands as one change to the
// ranking, not a per-page migration.
export const familyOf = pageKey => (PAGE_DEFS[pageKey] && PAGE_DEFS[pageKey].family) || "";

// Which registered pages are DESTINATIONS. Default true; a page marked `navigable: false` is one Otto can be
// ON but can never send a user TO (it is reached by opening a parent record). The otto-sync page/nav key gate
// reads this so a non-destination page is not reported as a missing catalog entry.
export const PAGE_DEFS_NAVIGABLE = Object.keys(PAGE_DEFS).reduce((acc, id) => {
    acc[id] = PAGE_DEFS[id].navigable !== false;
    return acc;
}, {});

// True when `pageKey` is a surface Otto has a manifest entry for. Callers that gate behavior on a page's
// capabilities check this first and FAIL OPEN on an unknown page (a mount newer than this map must keep
// today's behavior, never get blocked by it).
export const isKnownPage = pageKey => !!(pageKey && PAGE_DEFS[pageKey]);

// The friendly label for a defined page ("dashboard" -> "FCA Dashboard"); the raw key when unknown.
export const pageLabelOf = pageKey => (PAGE_DEFS[pageKey] && PAGE_DEFS[pageKey].label) || pageKey || "";

// Does this page mount `capability`? (false for unknown pages — see isKnownPage).
export const pageMountsCapability = (pageKey, capability) =>
    !!(pageKey && PAGE_DEFS[pageKey] && (PAGE_DEFS[pageKey].capabilities || []).includes(capability));

// EVERY defined page that mounts `capability`, reachable or not — for naming where an action lives even
// when this user can't get there ("charts live on the FCA Dashboard, which I can't open for you here").
export const pagesMountingCapability = capability =>
    Object.keys(PAGE_DEFS).filter(id => (PAGE_DEFS[id].capabilities || []).includes(capability));

// ---- what a capability MEANS to the person using it -------------------------------------------------
// The user-facing gloss for the capability keys above, so Otto can TELL someone what it can do on the page
// they are standing on instead of leaving them to guess at an empty prompt box (the Actions button in the
// composer). Keyed by the SAME strings PAGE_DEFS.capabilities uses, so mounting a capability on a new page
// describes it there with no per-page edit — and a capability with no entry here is simply left undescribed
// rather than shown as a blank row.
//
// Each `blurb` is about the OUTCOME, not the mechanism: nobody cares that row filtering on a pure table page
// is table_view's column_filter rather than the `filters` capability, only that Otto can narrow the rows.
const CAPABILITY_INFO = {
    filters: {
        label: "Filter what you're looking at",
        // Deliberately NOT a list of example fields: the same capability is mounted over catalogs that share
        // no vocabulary (the recommendation fields here, the asset fields on the asset pages), so naming any
        // of them would be wrong on half the pages that show this line.
        blurb: "Narrow the data by any filter this page offers — describe what you want and Otto finds the fields and values."
    },
    charts: {
        label: "Change the charts",
        blurb: "Switch a chart's type, group it by a different field, sort it, keep only the top few, maximize it or export it."
    },
    table_view: {
        label: "Work with the table",
        blurb: "Sort by a column, and export the table to Excel."
    },
    dashboard_reset: {
        label: "Clear everything",
        blurb: "Drop every applied filter and put the dashboard back to its starting state."
    },
    navigate: {
        label: "Take you to another page",
        blurb: "Open any page you can reach by naming it, and offer you one tap back."
    }
};

// The one place a capability's meaning genuinely DEPENDS on the page it is mounted on. A page carrying
// `table_view` WITHOUT `filters` has no filter panel at all — there, row filtering IS table_view (its
// column_filter). Describing that page with the generic table blurb would hide the single most useful
// thing Otto does on it, so the wording is derived from the capability SET the same way isTableViewPage
// derives naming from it, rather than being declared per page. (Buildings/assetBuildings used to be this
// branch's example — FTO-95 gave them a real `filters` panel too, which correctly flips them OUT of it.)
const refineForPage = (capability, mounted) =>
    capability === "table_view" && !mounted.includes("filters")
        ? {
              label: "Filter and work with the table",
              blurb: "Narrow the rows in place by a column's values, sort by a column, and export the table to Excel."
          }
        : null;

// What Otto can ACTUALLY do on `pageKey`, ready to display: [{ key, label, blurb }].
//
// `wired` (optional) is the list of capabilities the MOUNT really handed executors for. PAGE_DEFS is the
// DECLARED truth and the mount's props are the LIVE one, and this returns the INTERSECTION: a capability
// that is declared but not wired is one we would promise and then not deliver, which is worse than staying
// quiet about it. An unrecognized page declares nothing, so it falls back to whatever is wired — a mount
// newer than this map still describes itself correctly (the isKnownPage fail-open rule).
//
// The mechanism split above is read off the DECLARED set, never the intersection: whether a page's row
// filtering is column_filter is a fact about the page, not about which props this particular mount passed.
export const describeCapabilities = (pageKey, wired) => {
    const declared = (PAGE_DEFS[pageKey] && PAGE_DEFS[pageKey].capabilities) || [];
    const mounted = declared.length ? declared : wired || [];
    const shown = wired ? mounted.filter(c => wired.includes(c)) : mounted;
    return shown
        .filter(c => CAPABILITY_INFO[c])
        .map(c => ({ key: c, ...CAPABILITY_INFO[c], ...(refineForPage(c, mounted) || {}) }));
};

// A page Otto knows to be a data GRID — one with the `table_view` capability mounted — is a "Table View", and
// Otto refers to it that way in every user-facing string ("Recommendations Table View", never "Recommendations
// page"). This is the SINGLE source of truth for that fact, derived straight from PAGE_DEFS.capabilities, so it
// follows the pages: mount table_view on a new page and Otto starts calling it a Table View everywhere, with no
// per-string edits (the "source affordances from the surface" rule). A page with no table_view stays "<label> page".
export const isTableViewPage = pageKey =>
    !!(pageKey && PAGE_DEFS[pageKey] && (PAGE_DEFS[pageKey].capabilities || []).includes("table_view"));

// True when a nav TARGET ({ key, label }) points at a table-view page.
export const isTableViewTarget = target => !!(target && isTableViewPage(target.key));

// The bare NOUN PHRASE for a destination, for prose that already supplies the article/preposition
// ("take you to the …?"):  table view -> "Recommendations Table View" (a proper name);  otherwise ->
// "Asset Management page" (no doubling for a label already ending in "page", e.g. "Landing Page").
export const pageReference = target => {
    const label = (target && target.label) || "";
    if (isTableViewTarget(target)) return `${label} Table View`;
    return /page$/i.test(label) ? label : `${label} page`;
};

// The SHORT label form — no article, no "page" suffix — for compact buttons ("Go to <place>") and transient
// statuses ("Opening <place>").  table view -> "Recommendations Table View";  otherwise -> the bare label.
export const shortPageLabel = target =>
    isTableViewTarget(target) ? pageReference(target) : (target && target.label) || "";

// An ACTION label for a destination button/choice ("Open"/"Go to"):  table view -> "Open Recommendations
// Table View" (proper name, no article);  otherwise -> "Open the Asset Management page" (natural article + "page").
export const pageActionLabel = (verb, target) =>
    isTableViewTarget(target) ? `${verb} ${pageReference(target)}` : `${verb} the ${pageReference(target)}`;

// Build the manifest for THIS user/mount: the current page (always) + every OTHER defined page the user can
// actually reach (present in the permission-scoped navTargets). Defensive: unknown pages/targets contribute
// nothing; the result is always a plain, JSON-able array.
//
// OPERABLE pages only, and deliberately so. This is the payload the backend router sees as
// `app_context.pages`: the classifier reads the CURRENT page's capabilities from it, and the planner dumps
// the whole array into its prompt. Tier-1/2 pages have no capabilities to scope a route by, and adding them
// would rewrite the planner's prompt input — a behavior change that has to be re-gated on the plan eval, not
// a side effect of registering a page Otto merely knows about. Tier-2 knowledge is frontend-side (the naming
// and ambiguity seams below) until a slice deliberately widens the payload. Emitting `family`/`entity` is
// harmless for the same reason it is useful later: the backend ignores fields it does not model.
export const buildPageManifest = ({ page, navTargets } = {}) => {
    const reachable = new Set();
    if (page && PAGE_DEFS[page]) reachable.add(page);
    (navTargets || []).forEach(t => {
        if (t && t.key && PAGE_DEFS[t.key]) reachable.add(t.key);
    });
    return Object.keys(PAGE_DEFS)
        .filter(id => reachable.has(id) && tierOf(id) === TIERS.OPERABLE)
        .map(id => ({ page: id, ...PAGE_DEFS[id] }));
};

// Every ENTITY NOUN the registry knows, keyed by its normalized PLURAL — the derived replacement for what
// used to be a hand-written list of "ambiguous container nouns" (crossCapabilityCatalog.CROSS_CAPABILITY_TERMS).
// Keying on the noun rather than the page is what makes same-named pages across families collapse into ONE
// question: the asset hub and the project hub both own "buildings", so the term carries BOTH navKeys and the
// user is asked once about buildings, not once per page. Registering a page with an `entity` is therefore the
// only edit needed to make its noun disambiguate everywhere.
//
//   { assets: { singular: "asset", plural: "assets", chartValue: null, navKeys: ["assetmanagement"], pages: [...] } }
export const entityTerms = () => {
    const terms = {};
    Object.keys(PAGE_DEFS).forEach(id => {
        const entity = PAGE_DEFS[id].entity;
        if (!entity || !entity.plural) return;
        const key = normalizeText(entity.plural);
        if (!key) return;
        const term = terms[key] || {
            singular: entity.singular,
            plural: entity.plural,
            chartValue: null,
            navKeys: [],
            pages: []
        };
        // First non-null wins: a noun's chart grouping is a property of the DASHBOARD's option list, not of
        // whichever page happens to be registered second.
        if (term.chartValue == null && entity.chartValue != null) term.chartValue = entity.chartValue;
        // The nav-catalog target key IS the page key (one key space, asserted by xrepo-page-nav-keys).
        term.navKeys.push(id);
        term.pages.push(id);
        terms[key] = term;
    });
    return terms;
};

// Which reachable pages does this utterance NAME? ("export the RECOMMENDATIONS", "sort the BUILDINGS table".)
// Naming a page is an ANSWER the user already gave, so every seam that would otherwise ask "which page?" must
// consult this first — a seam that ignores it asks a question it has the answer to, and the list it asks with
// grows with every page we mount. Shared by the page-choice prompt and the capability-redirect seam so both
// ask/skip-asking on the SAME rule.
//
// The rule is nav grounding's own: the target's label + synonyms, matched as WHOLE words against normalized
// text — so a new page becomes nameable everywhere at once by extending navCatalog's synonyms, not by editing
// callers. Reachability is inherited from navTargets (permission-scoped) and targets the manifest doesn't know
// are dropped, so the result is always pages Otto can both reason about and open. Order follows navTargets.
//
// `minTier` scopes WHICH registered pages may be named. It exists because "named a page" and "named a page's
// DATA" are different questions with different blast radii: a seam that must pick ONE page to act on (the
// data-redirect) bails when the utterance names several, so every page added to the registry would otherwise
// widen its ambiguity and silently switch it off — "export the recommendations of florida region" names both
// Recommendations and Regions. Those seams pass TIERS.OPERABLE and see only pages Otto can actually act on;
// the default (any registered page) stays right for seams that only need the name.
export const pagesNamedIn = (utterance, navTargets, { minTier = TIERS.NAVIGABLE } = {}) => {
    const norm = ` ${normalizeText(utterance)} `;
    if (!norm.trim()) return [];
    const seen = new Set();
    return (navTargets || []).filter(t => {
        if (!t || !t.key || !PAGE_DEFS[t.key] || seen.has(t.key)) return false;
        if (tierOf(t.key) < minTier) return false;
        const names = [normalizeText(t.label), ...(t.synonyms || []).map(normalizeText)].filter(Boolean);
        if (!names.some(n => n && norm.includes(` ${n} `))) return false;
        seen.add(t.key);
        return true;
    });
};
